import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  AnimatedSpinner,
  Box,
  Button,
  Flex,
  Grid,
  Link,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  TabText,
  VStack,
} from '@tq/ui-components';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import { Form, FormProvider } from '@tq/ui-components/dist/components/forms-engine';
import { useIsMobile } from '@tq/ui-components/dist/hooks';

import { useConsultation } from 'api';
import { AddNote, AddNoteButton, Notes, NotesHeading, NotesInfoMessage } from 'components';
import { useNavigate } from 'hooks';
import { isInspection, isRpc } from 'tools';

import {
  PastResponses,
  StandardResponse,
  RpcOtherResponses,
  RpcPastResponses,
  RpcResponse,
} from '../../components';
import { useResponse } from '../../hooks';

export const OpenConsultationResponse: React.FC = () => {
  const { t } = useTranslation('consultation_response');
  const { navigateToSummary } = useNavigate();

  const { id } = useParams();
  const consultationId = +(id ?? 0);
  const { data: consultation, isLoading } = useConsultation(consultationId);
  const isMobile = useIsMobile();

  const [isShowingNotes, setIsShowingNotes] = useState(false);
  const { canSubmit, submitResponse } = useResponse(consultationId);

  const isRpcOrInspection = isRpc(consultation) || isInspection(consultation);

  const notesSection = (
    <Box width={{ base: '100%', lg: '420px' }}>
      <Flex justifyContent='space-between' alignItems='center' px={{ base: 0, lg: 4 }}>
        {isMobile ? (
          <VStack spacing={2}>
            <NotesInfoMessage consultation={consultation} />
            <AddNoteButton consultation={consultation} />
          </VStack>
        ) : (
          <>
            <NotesHeading consultation={consultation} />
            <AddNoteButton consultation={consultation} />
            <Link fontSize='sm' onClick={() => setIsShowingNotes(false)}>
              {t('hide', 'Hide')}
            </Link>
          </>
        )}
      </Flex>
      <Box border={`1px solid ${COLOURS.gray[300]}`} mt={6} p={4}>
        <AddNote />
        <Notes notes={consultation?.notes} loading={isLoading} />
      </Box>
    </Box>
  );

  if (isLoading) {
    return (
      <Flex justifyContent='center'>
        <AnimatedSpinner size='lg' />
      </Flex>
    );
  }

  return (
    <>
      <Flex gridGap={isShowingNotes ? 6 : 0} data-testid='consultation-response-tabs'>
        <FormProvider>
          <Form style={{ width: '100%' }}>
            <Grid width='100%' p={0}>
              <Tabs variant={isMobile ? 'line' : 'enclosed'} p={0} mx={{ base: -4, lg: 0 }}>
                <TabList>
                  <Tab
                    w={{ base: '32%', lg: 'auto' }}
                    mr={{ base: 0, lg: 1 }}
                    fontSize={{ base: 'unset', lg: 20 }}
                  >
                    <TabText>{t('my_response', 'My response')}</TabText>
                  </Tab>
                  <Tab w={{ base: '33%', lg: 'auto' }} fontSize={{ base: 'unset', lg: 20 }}>
                    <TabText>{t('past_responses', 'Past responses')}</TabText>
                  </Tab>
                  {isMobile && (
                    <Tab w={{ base: '33%', lg: '0' }} fontSize={{ base: 'unset', lg: 20 }}>
                      <TabText onClick={() => setIsShowingNotes(true)}>
                        {t('notes', 'Notes')}
                      </TabText>
                    </Tab>
                  )}
                  {!isMobile && (
                    <Box
                      style={{
                        marginLeft: 'auto',
                        alignSelf: 'center',
                        marginRight: '2%',
                      }}
                    >
                      {!isShowingNotes && (
                        <Link fontSize='sm' onClick={() => setIsShowingNotes(true)}>
                          {t('show_notes', 'Show notes')}
                        </Link>
                      )}
                    </Box>
                  )}
                </TabList>
                <TabPanels>
                  <TabPanel
                    data-testid='my-response-tab-panel'
                    py={0}
                    px={{ base: 4, lg: 0 }}
                    border={{ base: 'none', lg: `1px solid ${COLOURS.gray[400]}` }}
                  >
                    {consultation &&
                      (isRpcOrInspection ? (
                        <RpcResponse consultation={consultation} />
                      ) : (
                        <StandardResponse consultation={consultation} />
                      ))}
                  </TabPanel>
                  <TabPanel
                    data-testid='past-responses-tab-panel'
                    py={{ base: 0, lg: 2 }}
                    border={{ base: 'none', lg: `1px solid ${COLOURS.gray[400]}` }}
                  >
                    {consultation &&
                      (isRpcOrInspection ? (
                        <RpcPastResponses consultation={consultation} />
                      ) : (
                        <PastResponses
                          consultation={consultation}
                          hideTypeDefinition={isShowingNotes}
                        />
                      ))}
                  </TabPanel>
                  {isMobile && (
                    <TabPanel border={{ base: 'none', lg: `1px solid ${COLOURS.gray[400]}` }}>
                      {isShowingNotes ? notesSection : null}
                    </TabPanel>
                  )}
                </TabPanels>
                {isRpcOrInspection && !isShowingNotes && <RpcOtherResponses />}
              </Tabs>
            </Grid>
          </Form>
        </FormProvider>
        {isShowingNotes && !isMobile && <Box>{notesSection}</Box>}
      </Flex>
      {isMobile ? (
        <Box m={{ base: 'unset', lg: 6 }}>
          <Button
            mb={6}
            fontSize='sm'
            variant='primary'
            w='100%'
            minWidth='120px'
            isDisabled={!canSubmit}
            onClick={submitResponse}
          >
            {t('submit', 'Submit')}
          </Button>
          <Button
            fontSize='sm'
            variant='secondary'
            w='100%'
            minWidth='120px'
            onClick={() => consultation && navigateToSummary(consultation.id)}
          >
            {t('back_to_summary', 'Summary')}
          </Button>
        </Box>
      ) : (
        <Button srOnly isDisabled={!canSubmit} onClick={submitResponse}>
          {t('submit_response', 'Submit response')}
        </Button>
      )}
    </>
  );
};
